import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import {
  MonitorPlay,
  RefreshCw,
  Save,
  Bot,
  Film,
  Upload,
  ExternalLink,
  Trash2,
  Download,
  PlayCircle,
  Link2,
  CheckCircle2,
  AlertCircle,
  Copy,
  Scissors,
  ListOrdered
} from "lucide-react";
import { Button } from "./ui/button";

// Pestaña «Vídeos largos» del canal en inglés (solo admin). Solo /api/admin/en/*: nada de los otros canales.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/admin/en`;

const authHeaders = () => {
  const token = localStorage.getItem("cortoviral_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};
const opts = () => ({ headers: authHeaders(), withCredentials: true });

const STATUS = {
  writing: { label: "Escribiendo guion", style: "bg-cyan-500/15 text-cyan-300 border-cyan-500/30", busy: true },
  voicing: { label: "Grabando voz", style: "bg-cyan-500/15 text-cyan-300 border-cyan-500/30", busy: true },
  queued: { label: "En cola de render", style: "bg-slate-700/40 text-slate-300 border-white/10", busy: true },
  rendering: { label: "Montando", style: "bg-cyan-500/15 text-cyan-300 border-cyan-500/30", busy: true },
  uploading: { label: "Subiendo", style: "bg-cyan-500/15 text-cyan-300 border-cyan-500/30", busy: true },
  ready: { label: "Listo, sin subir", style: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  published: { label: "Publicado", style: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
  failed: { label: "Fallido", style: "bg-red-500/15 text-red-300 border-red-500/30" }
};

const CATEGORIES = {
  ai_tools: "Herramientas de IA", tech_tips: "Trucos de tecnología", money: "Dinero inteligente",
  business: "Negocios online", productivity: "Productividad", future: "Futuro y ciencia", gadgets: "Gadgets"
};

const mmss = (s) => (s ? `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, "0")}` : "—");

export const EnglishLongView = () => {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [create, setCreate] = useState({ topic: "", category: "ai_tools", minutes: 8, teasers: 2, upload: true });
  const [working, setWorking] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [preview, setPreview] = useState(null);
  const timer = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/long`, opts());
      setData(res.data);
      setForm((f) => f || res.data.settings);
    } catch (e) {
      setFeedback({ type: "error", text: e.response?.data?.detail || "No se pudieron cargar los vídeos largos." });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const anyBusy = data && (data.busy || data.videos.some((v) => STATUS[v.status]?.busy));
  useEffect(() => {
    clearInterval(timer.current);
    if (anyBusy) timer.current = setInterval(load, 5000);
    return () => clearInterval(timer.current);
  }, [anyBusy, load]);

  const run = async (key, fn, okText) => {
    setWorking(key);
    setFeedback(null);
    try {
      await fn();
      if (okText) setFeedback({ type: "success", text: okText });
    } catch (e) {
      setFeedback({ type: "error", text: e.response?.data?.detail || "Ha habido un error." });
    } finally {
      setWorking((w) => (w === key ? null : w));
    }
  };

  const saveConfig = (patch) => run("config", async () => {
    const body = patch || {
      long_minutes: Number(form.long_minutes),
      long_every_hours: Number(form.long_every_hours),
      teasers_per_long: Number(form.teasers_per_long)
    };
    const res = await axios.post(`${API}/radar/config`, body, opts());
    const s = res.data.settings;
    const next = { long_autopilot: s.long_autopilot, long_minutes: s.long_minutes, long_every_hours: s.long_every_hours,
      teasers_per_long: s.teasers_per_long, shorts_per_day: s.shorts_per_day, autopilot_enabled: s.autopilot_enabled };
    setForm(next);
    load();
  }, patch ? null : "Configuración guardada.");

  const createLong = () => run("create", async () => {
    await axios.post(`${API}/long/create`, { ...create, minutes: Number(create.minutes), teasers: Number(create.teasers) }, opts());
    setCreate((c) => ({ ...c, topic: "" }));
    load();
  }, "Creando el vídeo largo. Tarda un rato (guion, voz y montaje): verás el avance abajo.");

  const uploadLong = (v) => run(`up-${v.id}`, async () => {
    await axios.post(`${API}/long/${v.id}/upload`, {}, opts());
    load();
  }, "Subiendo el vídeo largo. Después se crean sus Shorts-avance.");

  const makeTeasers = (v) => run(`tz-${v.id}`, async () => {
    await axios.post(`${API}/long/${v.id}/teasers`, {}, opts());
    load();
  }, "Creando los Shorts-avance de este vídeo.");

  const deleteLong = (v) => {
    if (!window.confirm(`¿Borrar «${v.title}» de CortoViral? (Si ya está en YouTube, allí se queda.)`)) return;
    run(`del-${v.id}`, async () => {
      await axios.delete(`${API}/long/${v.id}`, opts());
      load();
    });
  };

  const markLinked = (p) => run(`ln-${p.id}`, async () => {
    await axios.post(`${API}/videos/${p.id}/linked`, { done: true }, opts());
    load();
  });

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    setFeedback({ type: "success", text: "Copiado." });
  };

  if (!data || !form) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-slate-400 text-sm gap-2">
        <span className="flex items-center gap-2"><RefreshCw className="w-4 h-4 animate-spin" /> Cargando vídeos largos...</span>
        {feedback && <span className="text-red-400 text-xs">{feedback.text}</span>}
      </div>
    );
  }

  const s = data.settings;
  const perDay = (s.long_autopilot ? (24 / Math.max(12, s.long_every_hours)) * (1 + s.teasers_per_long) : 0)
    + (s.autopilot_enabled ? s.shorts_per_day : 0);

  return (
    <div data-testid="en-long-view" className="space-y-6">
      <div className="bg-gradient-to-br from-[#12141D] to-[#0B1A2A] border border-cyan-500/30 rounded-2xl p-6 shadow-2xl space-y-4">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-cyan-500/15 border border-cyan-500/40 text-cyan-300 flex items-center justify-center shrink-0">
              <MonitorPlay className="w-6 h-6" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-lg font-black text-white">Vídeos largos · Clever Circuit</h3>
                <span className="text-[10px] uppercase font-mono font-bold px-2 py-0.5 rounded-full bg-cyan-500/15 text-cyan-300 border border-cyan-500/30">Solo admin</span>
              </div>
              <p className="text-xs text-slate-400 mt-0.5 max-w-2xl">
                Vídeos horizontales de 6-10 minutos con capítulos, voz en off y miniatura propia: es donde está el dinero.
                Al publicarse, se crean sus Shorts-avance, que llevan al vídeo largo. Solo en el canal en inglés.
              </p>
            </div>
          </div>
          <span className="text-[11px] text-slate-400 font-mono">Subidas hoy: {data.uploads_today}/{data.max_uploads}</span>
        </div>

        {feedback && (
          <div className={`p-3 rounded-xl text-xs flex items-start gap-2 break-words ${
            feedback.type === "success" ? "bg-emerald-500/10 border border-emerald-500/30 text-emerald-300" : "bg-red-500/10 border border-red-500/30 text-red-300"
          }`}>
            {feedback.type === "success" ? <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" /> : <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />}
            <span className="min-w-0">{feedback.text}</span>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="p-4 rounded-xl bg-black/40 border border-white/5 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <span className="text-[11px] uppercase font-bold text-slate-400 flex items-center gap-1.5"><Bot className="w-3.5 h-3.5 text-cyan-300" /> Piloto de vídeos largos</span>
              <button
                data-testid="en-long-autopilot-toggle"
                role="switch"
                aria-checked={!!s.long_autopilot}
                onClick={() => saveConfig({ long_autopilot: !s.long_autopilot })}
                disabled={working !== null}
                className={`w-10 h-6 rounded-full relative transition-colors shrink-0 ${s.long_autopilot ? "bg-emerald-500" : "bg-slate-700"}`}
              >
                <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${s.long_autopilot ? "left-5" : "left-1"}`} />
              </button>
            </div>
            <p className="text-xs text-white">
              {s.long_autopilot
                ? `1 vídeo largo cada ${s.long_every_hours} h con el mejor tema del Radar English, y ${s.teasers_per_long} Shorts-avance de cada uno`
                : "Apagado"}
            </p>
            {s.long_autopilot && data.next_long && (
              <p className="text-[10px] text-slate-500">Próximo: {new Date(data.next_long).toLocaleString("es-ES", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "2-digit" })}</p>
            )}
            <div className="grid grid-cols-3 gap-2">
              <label className="block">
                <span className="text-[10px] uppercase font-bold text-slate-500 block mb-1">Duración</span>
                <select value={form.long_minutes} onChange={(e) => setForm((f) => ({ ...f, long_minutes: Number(e.target.value) }))}
                  className="w-full bg-black/40 border border-white/10 rounded-lg px-2 py-2 text-xs text-white">
                  {Object.entries(data.minutes).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="text-[10px] uppercase font-bold text-slate-500 block mb-1">Cada (horas)</span>
                <input type="number" min={12} max={168} value={form.long_every_hours}
                  onChange={(e) => setForm((f) => ({ ...f, long_every_hours: e.target.value }))}
                  className="w-full bg-black/40 border border-white/10 rounded-lg px-2 py-2 text-xs text-white" />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase font-bold text-slate-500 block mb-1">Shorts-avance</span>
                <select value={form.teasers_per_long} onChange={(e) => setForm((f) => ({ ...f, teasers_per_long: Number(e.target.value) }))}
                  className="w-full bg-black/40 border border-white/10 rounded-lg px-2 py-2 text-xs text-white">
                  {[0, 1, 2, 3].map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </label>
            </div>
            <Button size="sm" onClick={() => saveConfig()} disabled={working !== null}
              className="bg-slate-100 hover:bg-white text-slate-950 font-bold text-xs flex items-center gap-1.5">
              <Save className="w-3.5 h-3.5" /> Guardar
            </Button>
          </div>
          <div className="p-4 rounded-xl bg-black/40 border border-white/5 text-xs text-slate-400 space-y-2">
            <p className="font-bold text-white">Cuota de YouTube</p>
            <p>
              Cada subida (Short o vídeo largo) gasta 1.600 de las 10.000 unidades diarias del proyecto. Con tu configuración salen
              unas <strong className={perDay > data.max_uploads ? "text-amber-300" : "text-white"}>{perDay.toFixed(1).replace(".", ",")} subidas al día</strong>,
              y el tope es {data.max_uploads} (EN_MAX_DAILY_UPLOADS).
            </p>
            {perDay > data.max_uploads && (
              <p className="text-amber-300">Te pasas del tope: lo que no quepa se queda «listo, sin subir» hasta el día siguiente. Baja los Shorts al día del Piloto English o los avances.</p>
            )}
            <p>El Radar gasta aparte (unas 500 por escaneo) si no tiene su propia clave (EN_YOUTUBE_API_KEY de otro proyecto).</p>
          </div>
        </div>
      </div>

      <div className="bg-[#12141D] border border-white/10 rounded-2xl p-6 space-y-3">
        <h4 className="text-sm font-black text-white flex items-center gap-1.5"><Film className="w-4 h-4 text-cyan-300" /> Crear un vídeo largo sobre un tema</h4>
        <div className="flex flex-col lg:flex-row gap-2">
          <input type="text" maxLength={200} placeholder="En español o en inglés: el vídeo sale en inglés"
            value={create.topic} onChange={(e) => setCreate((c) => ({ ...c, topic: e.target.value }))}
            className="flex-1 bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-cyan-500/60" />
          <select value={create.category} onChange={(e) => setCreate((c) => ({ ...c, category: e.target.value }))}
            className="bg-black/40 border border-white/10 rounded-lg px-2 py-2 text-sm text-white">
            {Object.entries(CATEGORIES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <select value={create.minutes} onChange={(e) => setCreate((c) => ({ ...c, minutes: Number(e.target.value) }))}
            className="bg-black/40 border border-white/10 rounded-lg px-2 py-2 text-sm text-white">
            {Object.entries(data.minutes).map(([k]) => <option key={k} value={k}>{k} min</option>)}
          </select>
          <select value={create.teasers} onChange={(e) => setCreate((c) => ({ ...c, teasers: Number(e.target.value) }))}
            title="Shorts-avance que se crean al publicarlo"
            className="bg-black/40 border border-white/10 rounded-lg px-2 py-2 text-sm text-white">
            {[0, 1, 2, 3].map((n) => <option key={n} value={n}>{n} avances</option>)}
          </select>
          <label className="flex items-center gap-1.5 text-xs text-slate-300 px-1">
            <input type="checkbox" checked={create.upload} onChange={(e) => setCreate((c) => ({ ...c, upload: e.target.checked }))} className="accent-cyan-500 w-4 h-4" />
            Subir al terminar
          </label>
          <Button data-testid="en-long-create-btn" onClick={createLong} disabled={working !== null || data.busy || create.topic.trim().length < 5}
            title={data.busy ? "Ya se está creando un vídeo; espera a que termine" : ""}
            className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-black text-xs flex items-center gap-1.5">
            {working === "create" ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Film className="w-3.5 h-3.5" />}
            Crear vídeo largo
          </Button>
        </div>
        <p className="text-[10px] text-slate-500">Tarda bastante: guion por capítulos, unos 60 audios de voz y el montaje (en el servidor, 10-20 minutos con prioridad baja para no frenar nada).</p>
      </div>

      {data.pending_links.length > 0 && (
        <div className="bg-[#12141D] border border-amber-400/30 rounded-2xl p-6 space-y-3">
          <h4 className="text-sm font-black text-white flex items-center gap-1.5"><Link2 className="w-4 h-4 text-amber-300" /> Shorts por enlazar ({data.pending_links.length})</h4>
          <p className="text-[11px] text-slate-400">
            YouTube no deja poner por la API el «Vídeo relacionado» de un Short (el botón que lleva al vídeo largo), así que es un paso tuyo de 10 segundos:
            pulsa <strong className="text-slate-200">Abrir en Studio</strong> → en <strong className="text-slate-200">Vídeo relacionado</strong> elige el vídeo largo que se indica → <strong className="text-slate-200">Guardar</strong>, y aquí, <strong className="text-slate-200">Ya enlazado</strong>.
            Mientras tanto, el Short ya dice en pantalla «Full video on the channel» y lleva el enlace en la descripción.
          </p>
          <div className="space-y-2">
            {data.pending_links.map((p) => (
              <div key={p.id} className="bg-black/30 border border-white/5 rounded-xl p-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2 min-w-0">
                <div className="min-w-0">
                  <p className="text-sm font-bold text-white break-words">{p.title}</p>
                  <p className="text-[11px] text-slate-400 break-words">Vídeo relacionado: <span className="text-cyan-300">{p.long_title}</span></p>
                </div>
                <div className="flex flex-wrap gap-1.5 shrink-0">
                  {p.studio_url && (
                    <a href={p.studio_url} target="_blank" rel="noopener noreferrer"
                      className="h-7 px-2.5 text-[11px] font-bold text-slate-950 bg-amber-400 hover:bg-amber-300 flex items-center gap-1 rounded-md">
                      <ExternalLink className="w-3 h-3" /> Abrir en Studio
                    </a>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => copy(p.long_title || "")}
                    className="h-7 px-2 text-[11px] text-slate-300 hover:text-white flex items-center gap-1">
                    <Copy className="w-3 h-3" /> Copiar título
                  </Button>
                  <Button size="sm" onClick={() => markLinked(p)} disabled={working !== null}
                    className="h-7 px-2.5 text-[11px] font-bold bg-emerald-500 hover:bg-emerald-400 text-slate-950 flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" /> Ya enlazado
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-black text-white">Vídeos largos ({data.videos.length})</h4>
          <Button variant="ghost" size="sm" onClick={load} className="h-7 text-xs text-slate-400 hover:text-white flex items-center gap-1">
            <RefreshCw className={`w-3.5 h-3.5 ${anyBusy ? "animate-spin" : ""}`} /> Actualizar
          </Button>
        </div>
        {data.videos.length === 0 ? (
          <div className="text-center py-12 text-sm text-slate-500 bg-[#12141D] border border-white/5 rounded-2xl">
            Todavía no hay vídeos largos. Crea uno arriba o enciende el Piloto de vídeos largos.
          </div>
        ) : (
          <div className="space-y-2">
            {data.videos.map((v) => {
              const st = STATUS[v.status] || STATUS.failed;
              const hasFile = v.video_url && !v.video_deleted;
              return (
                <div key={v.id} className="bg-[#12141D] border border-white/10 rounded-xl p-3 space-y-2 min-w-0">
                  <div className="flex flex-col sm:flex-row gap-3 min-w-0">
                    {v.thumb_url ? (
                      <img src={`${BACKEND_URL || ""}${v.thumb_url}`} alt="" loading="lazy" className="w-full sm:w-44 aspect-video object-cover rounded-lg bg-slate-800 shrink-0" />
                    ) : (
                      <div className="w-full sm:w-44 aspect-video rounded-lg bg-slate-800 shrink-0 flex items-center justify-center text-slate-600"><MonitorPlay className="w-8 h-8" /></div>
                    )}
                    <div className="min-w-0 flex-1 space-y-1">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded border ${st.style}`}>
                          {st.busy && <RefreshCw className="w-2.5 h-2.5 inline mr-1 animate-spin" />}{st.label}
                        </span>
                        <span className="text-[10px] uppercase font-bold text-cyan-300">{CATEGORIES[v.category] || v.category}</span>
                        <span className="text-[10px] text-slate-500">{v.source === "radar" ? "Del Radar" : "Manual"} · {new Date(v.created_at).toLocaleString("es-ES")}</span>
                        {v.duration && <span className="text-[10px] text-slate-500">· {mmss(v.duration)}</span>}
                        {v.words && <span className="text-[10px] text-slate-500">· {v.words} palabras</span>}
                      </div>
                      <p className="text-sm font-bold text-white break-words">{v.title}</p>
                      {v.progress && <p className={`text-[11px] break-words ${v.status === "failed" ? "text-red-400" : "text-slate-400"}`}>{v.progress}</p>}
                      {v.thumbnail_error && <p className="text-[11px] text-amber-300 break-words">{v.thumbnail_error}</p>}
                      {v.chapter_titles?.length > 0 && (
                        <p className="text-[11px] text-slate-500 flex items-start gap-1 break-words">
                          <ListOrdered className="w-3 h-3 shrink-0 mt-0.5" /> {v.chapter_titles.join(" · ")}
                        </p>
                      )}
                      {v.teaser_ids?.length > 0 && <p className="text-[11px] text-slate-500">{v.teaser_ids.length} Shorts-avance (en la pestaña Canal English)</p>}
                      <div className="flex flex-wrap gap-1.5 pt-1">
                        {hasFile && (
                          <Button size="sm" variant="ghost" onClick={() => setPreview(preview === v.id ? null : v.id)}
                            className="h-7 px-2 text-[11px] text-slate-300 hover:text-white flex items-center gap-1">
                            <PlayCircle className="w-3 h-3" /> {preview === v.id ? "Ocultar" : "Ver"}
                          </Button>
                        )}
                        {hasFile && (
                          <a href={`${API}/long/${v.id}/file`} className="h-7 px-2 text-[11px] text-slate-300 hover:text-white flex items-center gap-1 rounded-md hover:bg-white/5">
                            <Download className="w-3 h-3" /> Descargar
                          </a>
                        )}
                        {v.status === "ready" && hasFile && (
                          <Button size="sm" onClick={() => uploadLong(v)} disabled={working !== null || data.busy || !data.channel_connected}
                            title={data.channel_connected ? "" : "Conecta el canal en inglés"}
                            className="h-7 px-2.5 text-[11px] font-bold bg-cyan-500 hover:bg-cyan-400 text-slate-950 flex items-center gap-1">
                            {working === `up-${v.id}` ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                            Subir a Clever Circuit
                          </Button>
                        )}
                        {v.status === "published" && !(v.teaser_ids?.length) && (
                          <Button size="sm" onClick={() => makeTeasers(v)} disabled={working !== null || data.busy}
                            className="h-7 px-2.5 text-[11px] font-bold bg-slate-800 hover:bg-slate-700 text-white flex items-center gap-1">
                            <Scissors className="w-3 h-3" /> Crear Shorts-avance
                          </Button>
                        )}
                        {v.yt_url && (
                          <a href={v.yt_url} target="_blank" rel="noopener noreferrer"
                            className="h-7 px-2.5 text-[11px] font-bold text-emerald-300 hover:text-emerald-200 flex items-center gap-1 rounded-md bg-emerald-500/10 border border-emerald-500/30">
                            <ExternalLink className="w-3 h-3" /> Ver en YouTube
                          </a>
                        )}
                        {!st.busy && (
                          <Button size="sm" variant="ghost" onClick={() => deleteLong(v)} disabled={working !== null}
                            className="h-7 px-2 text-[11px] text-slate-500 hover:text-red-300 flex items-center gap-1">
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                  {preview === v.id && hasFile && (
                    <video src={`${API}/long/${v.id}/file`} controls className="w-full max-w-2xl aspect-video rounded-lg bg-black" />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
